/**
 * 风格缩略图 tile — 2026-06-01
 *
 * 4:5 卡片:确定性渐变占位打底 + 懒加载样例图淡入(图缺失/未就绪时 onError 退回渐变,
 * 不留破图)。选中态 = 主色环 + ✓ badge。侧栏「热门」与画廊网格共用。
 *
 * a11y / 性能(ui-ux-pro-max):loading=lazy + 固定宽高比防 CLS、alt 文本、aria-pressed、
 * cursor-pointer、focus-visible 环;hover 仅图片在 overflow-hidden 内轻微放大,tile
 * 尺寸不变(不触发 layout shift)。
 */
import { useState } from "react";
import { Check } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import {
  type StyleOption,
  stylePlaceholderGradient,
  styleSampleUrl,
} from "../../../ui/components/free-create/styleOptions";

interface Props {
  option: StyleOption;
  selected: boolean;
  onSelect: () => void;
  /** sm:侧栏热门横排;md:画廊网格(显示 blurb) */
  size?: "sm" | "md";
  className?: string;
}

export function StyleThumb({ option, selected, onSelect, size = "md", className }: Props) {
  const [imgFailed, setImgFailed] = useState(false);
  const src = styleSampleUrl(option.key);
  const showImg = Boolean(src) && !imgFailed;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      title={option.blurb || option.label}
      className={cn(
        "group/style relative flex flex-col overflow-hidden rounded-lg border text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
        selected
          ? "border-primary ring-2 ring-primary"
          : "border-border/60 hover:border-primary/50",
        className
      )}
    >
      {/* 4:5 缩略图区 */}
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: "4 / 5", background: stylePlaceholderGradient(option.key) }}
      >
        {showImg && (
          <img
            src={src}
            alt={`${option.label} 风格样例`}
            loading="lazy"
            decoding="async"
            onError={() => setImgFailed(true)}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-200 group-hover/style:scale-105"
          />
        )}
        {/* 渐变占位上的风格名水印(图未就绪时给个可读提示) */}
        {!showImg && (
          <span className="absolute inset-0 flex items-center justify-center px-1 text-center text-[13px] font-semibold text-white/95 drop-shadow">
            {option.label}
          </span>
        )}
        {/* 选中勾标 */}
        {selected && (
          <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
            <Check className="h-3 w-3" />
          </span>
        )}
      </div>

      {/* label + (md 时) blurb */}
      <div className={cn("px-1.5 py-1", size === "sm" ? "text-center" : "")}>
        <div
          className={cn(
            // 2026-08-26 同一条:画风名是必须看清才能选的东西。13px → 14px,
            // 并把 truncate 换成折两行 —— 宁可高一点,不许把名字切掉。
            "line-clamp-2 text-sm font-medium leading-tight",
            selected ? "text-primary" : "text-foreground"
          )}
        >
          {option.label}
        </div>
        {size === "md" && option.blurb && (
          <div className="mt-0.5 line-clamp-1 text-2xs text-muted-foreground">
            {option.blurb}
          </div>
        )}
      </div>
    </button>
  );
}
